import { useState } from 'react'
import { useMutation, type useQueryClient } from '@tanstack/react-query'
import { LOCALE_NATIVE_NAMES, OFFICIAL_LANGUAGES } from '@self-restaurant/shared'
import { config, type RestaurantConfig } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { Button, Card, Select, toast } from './ui/index.js'

// ─── Idioma principal ─────────────────────────────────────────────────────────

// #419: as 14 línguas oficiais, todas com código base (sem região), autónimos
// da FONTE ÚNICA em shared (#428). A moeda NÃO deriva do idioma — vem do país
// do tenant (ver Waiter/Menu: cfg.currency).
const LOCALE_OPTIONS = OFFICIAL_LANGUAGES.map((code) => ({
  value: code,
  label: LOCALE_NATIVE_NAMES[code],
}))

/**
 * Idioma principal do restaurante — base das traduções do cardápio e moeda
 * apresentada. Movido do Branding para Traduções (#336): é configuração de
 * idiomas, não identidade visual. Mesmas chaves admin.branding.* (sem drift
 * de textos no banco).
 */
export function LocaleForm({
  cfg,
  qc,
}: {
  cfg: RestaurantConfig
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const [locale, setLocale] = useState(cfg.primary_locale)

  const mut = useMutation({
    mutationFn: (primary_locale: string) => config.update({ primary_locale }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.locale_updated_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Card title={t('admin.branding.locale_title')} description={t('admin.branding.locale_desc')}>
      <div className="space-y-4">
        <Select
          label={t('admin.branding.primary_locale')}
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          options={LOCALE_OPTIONS}
          optionLang
        />
        <Button
          loading={mut.isPending}
          disabled={locale === cfg.primary_locale}
          onClick={() => mut.mutate(locale)}
        >
          {t('admin.branding.save_locale')}
        </Button>
      </div>
    </Card>
  )
}
